import * as React from 'react';
import { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  Alert,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  FormHelperText,
  Unstable_Grid2 as Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Controller, useForm } from 'react-hook-form';
import { z as zod } from 'zod';

import type { Unit } from '@/types/v2/unit'; // Ensure this is correct for your context
import type { UnitType } from '@/types/v2/unit-type';
import axios from '@/lib/v2/axios';
import { toTitleCase } from '@/lib/v2/typography/to-title-case';

const schema = zod.object({
  address: zod.string().min(1, 'An address is required'),
  address2: zod.string().optional(),
  city: zod.string().min(1, 'A city is required'),
  state: zod.string().min(1, 'A state is required'),
  zip_code: zod
    .string()
    .min(1, 'A zip code is required')
    .regex(/^\d{5}$/, 'Invalid zip code'), // Validate zip code format (5 digits)
  // unit_type: zod
  //   .string() // Change from number to string for unit_type
  //   .min(1, 'A unit type is required'),
  unit_number: zod.string().optional(),
  floor: zod.string().optional(),
  construction_date: zod.date().optional(),
});
type Values = zod.infer<typeof schema>;



interface CreateOrEditUnitFormProps {
  onSuccess: (data: Unit) => void; // Change to Unit
  unitTypes: UnitType[];
  initialData?: Values | Unit; // Change to Unit
  mode: 'create' | 'edit';
  unitId?: string | number; // Keep this for editing
  communityUnitTypes?: UnitType[];
  communityId?: string | number;
}

export function CreateOrEditUnitForm({
  onSuccess,
  unitTypes = [],
  initialData,
  unitId, // Change communityId to unitId
  mode,
  communityId,
  communityUnitTypes,
}: CreateOrEditUnitFormProps): React.JSX.Element {
  // Update the props type
  const [error, setError] = React.useState<string | null>(null);
  const [selectedUnitTypes, setSelectedUnitTypes] = useState<UnitType[]>([]); // Keep this for unit types

  const defaultValues = {
    address: initialData?.address || '',
    city: initialData?.city || '',
    state: initialData?.state || '',
    zip_code: initialData?.zip_code || '',
    // unit_type:
    //   typeof initialData?.unit_type === 'string' ? initialData.unit_type : initialData?.unit_type?.id.toString() || '', // Ensure unit_type is a string
    // unit_number: initialData?.unit_number || '',
    floor: initialData?.floor || '',
  } satisfies Values;

  // React.useEffect(() => {
  //   // Initialize selected unit types based on default values
  //   const initialSelectedUnitTypes = unitTypes.filter(
  //     (unit) => defaultValues.unit_type && Number(defaultValues.unit_type) === unit.id // Convert unit_type to number for comparison
  //   );
  //   setSelectedUnitTypes(initialSelectedUnitTypes);
  // }, []);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Values>({ defaultValues, resolver: zodResolver(schema) });

  const createUnitRequest = useMutation({
    mutationFn: async (data: Values) => {
      const newUnit = {
        community_id: communityId,
        // unit_type_id: data.unit_type,
        ...data,
      };

      if (mode === 'create') {
        await axios.get('/sanctum/csrf-cookie');
        return axios.post('/api/v2/units', newUnit); // Ensure endpoint is correct
      } 
        if (!unitId || !communityId) {
          console.error('Unit ID or community ID is required');
          setError('We encountered an issue while updating the unit. Please try again later.');
          return;
        }

        console.log('Updating unit', unitId); // Update log message
        await axios.get('/sanctum/csrf-cookie');
        return axios.put(`/api/v2/units/${unitId}`, newUnit); // Change endpoint to units
      
    },
  });

  const handleCreateOrEditUnit = useCallback(
    async (data: Values) => {
      try {
        const response = await createUnitRequest.mutateAsync(data); // Update to use createUnitRequest
        onSuccess(response?.data);
      } catch (error: any) {
        console.error(error);
        setError(error?.response?.data?.message || `An error occurred while ${mode}ing the unit`); // Update error message
      }
    },
    [createUnitRequest, selectedUnitTypes] // Keep dependencies
  );



  return (
    <Stack spacing={2}>
      {error ? <Alert severity="error">{error}</Alert> : null}
      <form onSubmit={handleSubmit(handleCreateOrEditUnit)}>
        {/* Update to handleCreateOrEditUnit */}
        <Stack spacing={2}>
          <Card>
            <CardHeader title="Unit Details" /> {/* Change title to Unit Details */}
            <CardContent>
              <Stack spacing={2}>
                <Grid container spacing={3}>
                  <Grid md={8} xs={12}>
                    <Controller
                      control={control}
                      name="address"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.address)} fullWidth>
                          <InputLabel required>Address</InputLabel>
                          <OutlinedInput disabled={createUnitRequest.isPending} {...field} />
                          {errors.address ? <FormHelperText>{errors.address.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  
                  <Grid md={4} xs={12}>
                    <Controller
                      control={control}
                      name="address2"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.address2)} fullWidth>
                          <InputLabel>Address 2 (Optional)</InputLabel>
                          <OutlinedInput disabled={createUnitRequest.isPending} {...field} />
                          {errors.address2 ? <FormHelperText>{errors.address2.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid md={4} xs={12}>
                    <Controller
                      control={control}
                      name="zip_code"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.zip_code)} fullWidth>
                          <InputLabel required>Zip Code</InputLabel>
                          <OutlinedInput disabled={createUnitRequest.isPending} {...field} />
                          {errors.zip_code ? <FormHelperText>{errors.zip_code.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid md={6} xs={12}>
                    <Controller
                      control={control}
                      name="city"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.city)} fullWidth>
                          <InputLabel required>City</InputLabel>
                          <OutlinedInput disabled={createUnitRequest.isPending} {...field} />
                          {errors.city ? <FormHelperText>{errors.city.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid md={6} xs={12}>
                    <Controller
                      control={control}
                      name="state"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.state)} fullWidth>
                          <InputLabel required>State</InputLabel>
                          <Select
                            {...field}
                            disabled={createUnitRequest.isPending}
                            label="State"
                          >
                            <MenuItem value="AL">Alabama</MenuItem>
                            <MenuItem value="AK">Alaska</MenuItem>
                            <MenuItem value="AZ">Arizona</MenuItem>
                            <MenuItem value="AR">Arkansas</MenuItem>
                            <MenuItem value="CA">California</MenuItem>
                            <MenuItem value="CO">Colorado</MenuItem>
                            <MenuItem value="CT">Connecticut</MenuItem>
                            <MenuItem value="DE">Delaware</MenuItem>
                            <MenuItem value="FL">Florida</MenuItem>
                            <MenuItem value="GA">Georgia</MenuItem>
                            <MenuItem value="HI">Hawaii</MenuItem>
                            <MenuItem value="ID">Idaho</MenuItem>
                            <MenuItem value="IL">Illinois</MenuItem>
                            <MenuItem value="IN">Indiana</MenuItem>
                            <MenuItem value="IA">Iowa</MenuItem>
                            <MenuItem value="KS">Kansas</MenuItem>
                            <MenuItem value="KY">Kentucky</MenuItem>
                            <MenuItem value="LA">Louisiana</MenuItem>
                            <MenuItem value="ME">Maine</MenuItem>
                            <MenuItem value="MD">Maryland</MenuItem>
                            <MenuItem value="MA">Massachusetts</MenuItem>
                            <MenuItem value="MI">Michigan</MenuItem>
                            <MenuItem value="MN">Minnesota</MenuItem>
                            <MenuItem value="MS">Mississippi</MenuItem>
                            <MenuItem value="MO">Missouri</MenuItem>
                            <MenuItem value="MT">Montana</MenuItem>
                            <MenuItem value="NE">Nebraska</MenuItem>
                            <MenuItem value="NV">Nevada</MenuItem>
                            <MenuItem value="NH">New Hampshire</MenuItem>
                            <MenuItem value="NJ">New Jersey</MenuItem>
                            <MenuItem value="NM">New Mexico</MenuItem>
                            <MenuItem value="NY">New York</MenuItem>
                            <MenuItem value="NC">North Carolina</MenuItem>
                            <MenuItem value="ND">North Dakota</MenuItem>
                            <MenuItem value="OH">Ohio</MenuItem>
                            <MenuItem value="OK">Oklahoma</MenuItem>
                            <MenuItem value="OR">Oregon</MenuItem>
                            <MenuItem value="PA">Pennsylvania</MenuItem>
                            <MenuItem value="RI">Rhode Island</MenuItem>
                            <MenuItem value="SC">South Carolina</MenuItem>
                            <MenuItem value="SD">South Dakota</MenuItem>
                            <MenuItem value="TN">Tennessee</MenuItem>
                            <MenuItem value="TX">Texas</MenuItem>
                            <MenuItem value="UT">Utah</MenuItem>
                            <MenuItem value="VT">Vermont</MenuItem>
                            <MenuItem value="VA">Virginia</MenuItem>
                            <MenuItem value="WA">Washington</MenuItem>
                            <MenuItem value="WV">West Virginia</MenuItem>
                            <MenuItem value="WI">Wisconsin</MenuItem>
                            <MenuItem value="WY">Wyoming</MenuItem>
                            <MenuItem value="DC">District of Columbia</MenuItem>
                          </Select>
                          {errors.state ? <FormHelperText>{errors.state.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {/* <Grid md={6} xs={12}>
                    <Controller
                      control={control}
                      name="unit_type"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.unit_type)} fullWidth>
                          <InputLabel required>Unit Type</InputLabel>
                          <Select
                            disabled={createUnitRequest.isPending}
                            {...field}
                            label="Unit Type"
                            labelId="unit-type-label"
                            id="unit-type-select"
                            onChange={(e) => {
                              field.onChange(e);
                              setValue('unit_type', e.target.value.toString());
                            }}
                          >
                            {communityUnitTypes?.map((unitType) => (
                              <MenuItem key={unitType.id} value={unitType.id}>
                                {toTitleCase(unitType.name)}
                              </MenuItem>
                            ))}
                          </Select>
                          {errors.unit_type ? <FormHelperText>{errors.unit_type.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid> */}
                  <Grid md={6} xs={12}>
                    <Controller
                      control={control}
                      name="unit_number"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.unit_number)} fullWidth>
                          <InputLabel>Unit Number</InputLabel>
                          <OutlinedInput disabled={createUnitRequest.isPending} {...field} />
                          {errors.unit_number ? <FormHelperText>{errors.unit_number.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid md={6} xs={12}>
                    <Controller
                      control={control}
                      name="floor"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.floor)} fullWidth>
                          <InputLabel>Floor</InputLabel>
                          <OutlinedInput disabled={createUnitRequest.isPending} {...field} />
                          {errors.floor ? <FormHelperText>{errors.floor.message}</FormHelperText> : null}
                        </FormControl>
                      )}
                    />
                  </Grid>
                  {/* <Grid md={6} xs={12}>
                    <Controller
                      control={control}
                      name="construction_date"
                      render={({ field }) => (
                        <FormControl error={Boolean(errors.construction_date)} fullWidth>
                          <InputLabel>Construction Date</InputLabel>
                          <DatePicker
                            sx={{ mt: 1 }}
                            disabled={createUnitRequest.isPending}
                            value={field.value ? dayjs(field.value) : null} // Convert Date to Dayjs
                            onChange={(date) => field.onChange(date ? date.toISOString() : undefined)} // Convert Dayjs to ISO string
                            name={field.name}
                            inputRef={field.ref}
                          />
                          {errors.construction_date ? (
                            <FormHelperText>{errors.construction_date.message}</FormHelperText>
                          ) : null}
                        </FormControl>
                      )}
                    />
                  </Grid> */}
                </Grid>
              </Stack>
            </CardContent>
          </Card>

          <LoadingButton
            disabled={createUnitRequest.isPending} // Update to createUnitRequest
            loading={createUnitRequest.isPending} // Update to createUnitRequest
            type="submit"
            variant="contained"
          >
            {mode === 'create' ? 'Create Unit' : 'Edit Unit'} {/* Update button text */}
          </LoadingButton>
        </Stack>
      </form>
    </Stack>
  );
}
